import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Plus, Trash2, AlertCircle, CheckCircle, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminCalendar = () => {
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newBlockedDate, setNewBlockedDate] = useState({
    date: '',
    reason: 'Jour bloqué'
  });

  useEffect(() => {
    fetchBlockedDates();
  }, []);

  const fetchBlockedDates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/blocked-dates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Trier par date
      const sorted = response.data.blockedDates.sort((a, b) => a.date.localeCompare(b.date));
      setBlockedDates(sorted);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
      toast.error('Erreur lors du chargement des dates bloquées');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlockedDate = async () => {
    if (!newBlockedDate.date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/blocked-dates`,
        newBlockedDate,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Date bloquée avec succès');
      setShowAddModal(false);
      setNewBlockedDate({ date: '', reason: 'Jour bloqué' });
      fetchBlockedDates();
    } catch (error) {
      console.error('Error blocking date:', error);
      if (error.response?.status === 400) {
        toast.error('Cette date est déjà bloquée');
      } else {
        toast.error('Erreur lors du blocage de la date');
      }
    }
  };

  const handleDeleteBlockedDate = async (blockedDateId, date) => {
    if (!window.confirm(`Voulez-vous vraiment débloquer la date du ${formatDate(date)} ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/blocked-dates/${blockedDateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Date débloquée avec succès');
      fetchBlockedDates();
    } catch (error) {
      console.error('Error deleting blocked date:', error);
      toast.error('Erreur lors du déblocage de la date');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A96E] mx-auto"></div>
          <p className="mt-4 text-[#5D4037]">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif font-bold text-[#2C1810] flex items-center gap-3">
            <CalendarIcon className="text-[#C9A96E]" size={32} />
            Gestion du Calendrier
          </h1>
          <p className="text-[#5D4037] mt-2">
            Bloquez des dates pour empêcher les clients de réserver
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-[#C9A96E] text-white rounded-lg hover:bg-[#A67B5B] transition-colors"
        >
          <Plus size={20} />
          Bloquer une date
        </button>
      </div>

      {/* Blocked Dates List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 bg-[#F5F0EB] border-b border-[#D4C4B5]">
          <h2 className="text-xl font-semibold text-[#2C1810]">
            Dates bloquées ({blockedDates.length})
          </h2>
        </div>

        {blockedDates.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarIcon className="mx-auto text-[#D4C4B5] mb-4" size={64} />
            <p className="text-[#5D4037] text-lg">Aucune date bloquée</p>
            <p className="text-[#A67B5B] mt-2">
              Cliquez sur "Bloquer une date" pour commencer
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#E8DDD4]">
            {blockedDates.map((blockedDate) => (
              <div
                key={blockedDate.id}
                className="p-6 hover:bg-[#FDF8F3] transition-colors flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="text-[#C9A96E]" size={20} />
                    <div>
                      <p className="font-semibold text-[#2C1810] text-lg">
                        {formatDate(blockedDate.date)}
                      </p>
                      <p className="text-[#5D4037] mt-1">
                        {blockedDate.reason}
                      </p>
                      <p className="text-sm text-[#A67B5B] mt-1">
                        Bloqué le {new Date(blockedDate.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteBlockedDate(blockedDate.id, blockedDate.date)}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={18} />
                  Débloquer
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-serif font-bold text-[#2C1810]">
                Bloquer une date
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewBlockedDate({ date: '', reason: 'Jour bloqué' });
                }}
                className="text-[#5D4037] hover:text-[#2C1810]"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2C1810] mb-2">
                  Date à bloquer *
                </label>
                <input
                  type="date"
                  min={getTodayDate()}
                  value={newBlockedDate.date}
                  onChange={(e) => setNewBlockedDate({ ...newBlockedDate, date: e.target.value })}
                  className="w-full px-4 py-3 border border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C1810] mb-2">
                  Raison
                </label>
                <input
                  type="text"
                  value={newBlockedDate.reason}
                  onChange={(e) => setNewBlockedDate({ ...newBlockedDate, reason: e.target.value })}
                  placeholder="Ex: Congés, Fermeture exceptionnelle..."
                  className="w-full px-4 py-3 border border-[#D4C4B5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A96E]"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewBlockedDate({ date: '', reason: 'Jour bloqué' });
                }}
                className="flex-1 px-6 py-3 border border-[#D4C4B5] text-[#2C1810] rounded-lg hover:bg-[#F5F0EB] transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleAddBlockedDate}
                className="flex-1 px-6 py-3 bg-[#C9A96E] text-white rounded-lg hover:bg-[#A67B5B] transition-colors"
              >
                Bloquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
